import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface Loan {
  id: string; item_id: string; member_id: string;
  title_en: string; catalogue_id: string; cover_image_url: string | null;
  membership_number: string; member_type: string;
  checkout_date: string; due_date: string; return_date: string | null;
  status: string; fine_amount: string; fine_paid: boolean;
  renewal_count: number; checkout_method: string;
}

const BN = {
  pageTitle: 'Pinjaman Perpustakaan',
  pageSubtitle: 'rekod',
  backLink: '← Laman Utama',
  checkoutBtn: '+ Pinjam Bahan',
  checkoutFormTitle: 'Pinjam Bahan',
  labelMemberId: 'ID Ahli *',
  labelItemId: 'ID Bahan *',
  labelMethod: 'Kaedah',
  confirmBtn: 'Sahkan',
  confirmingBtn: 'Meminjam…',
  cancelBtn: 'Batal',
  filterStatus: 'Status',
  filterMemberId: 'ID Ahli',
  filterAll: 'Semua',
  filterBtn: 'Tapis',
  retryBtn: 'Cuba Lagi',
  emptyTitle: 'Tiada Rekod Pinjaman',
  emptySubtitle: 'Tiada rekod pinjaman dengan penapis semasa.',
  returnBtn: 'Pulang',
  renewBtn: 'Baharui',
  payFineBtn: '💰 Bayar Denda',
  finePanelTitle: 'Rekod Bayaran Denda',
  finePanelOutstanding: 'tertunggak',
  fineAmountLabel: 'Jumlah Bayar',
  fineSaveBtn: '✓ Sahkan',
  fineRecordingBtn: 'Merekodkan…',
  overdueBadge: '⚠ LEWAT',
  finePaidBadge: '✓ Denda Dibayar',
  memberLabel: 'Ahli',
  checkedOutLabel: 'Dipinjam',
  dueLabel: 'Matang',
  returnedLabel: 'Dikembalikan',
  renewedLabel: 'Diperbaharui',
  prevBtn: '← Sebelum',
  nextBtn: 'Seterusnya →',
  statusLabels: {
    ACTIVE: 'Aktif',
    RETURNED: 'Dipulangkan',
    OVERDUE: 'Lewat',
    LOST: 'Hilang',
    RENEWAL_PENDING: 'Perlu Baharui',
  },
};

const EN = {
  pageTitle: 'Library Loans',
  pageSubtitle: 'records',
  backLink: '← Home',
  checkoutBtn: '+ Checkout',
  checkoutFormTitle: 'Checkout Item',
  labelMemberId: 'Member ID *',
  labelItemId: 'Item ID *',
  labelMethod: 'Method',
  confirmBtn: 'Confirm',
  confirmingBtn: 'Checking out…',
  cancelBtn: 'Cancel',
  filterStatus: 'Status',
  filterMemberId: 'Member ID',
  filterAll: 'All',
  filterBtn: 'Filter',
  retryBtn: 'Retry',
  emptyTitle: 'No Loan Records',
  emptySubtitle: 'No loan records found with current filters.',
  returnBtn: 'Return',
  renewBtn: 'Renew',
  payFineBtn: '💰 Pay Fine',
  finePanelTitle: 'Record Fine Payment',
  finePanelOutstanding: 'outstanding',
  fineAmountLabel: 'Amount',
  fineSaveBtn: '✓ Confirm',
  fineRecordingBtn: 'Recording…',
  overdueBadge: '⚠ OVERDUE',
  finePaidBadge: '✓ Fine Paid',
  memberLabel: 'Member',
  checkedOutLabel: 'Checked Out',
  dueLabel: 'Due',
  returnedLabel: 'Returned',
  renewedLabel: 'Renewed',
  prevBtn: '← Previous',
  nextBtn: 'Next →',
  statusLabels: {
    ACTIVE: 'Active',
    RETURNED: 'Returned',
    OVERDUE: 'Overdue',
    LOST: 'Lost',
    RENEWAL_PENDING: 'Renewal Pending',
  },
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#60a5fa', RETURNED: '#4ade80', OVERDUE: '#f87171',
  LOST: '#9ca3af', RENEWAL_PENDING: '#facc15',
};

const DEMO_SCHOOL = '3f868b12-b554-4f51-a893-69e9b4ba9f57';
function getSchoolId(user: AuthUser): string { return user.roles.find((r) => r.schoolId)?.schoolId ?? DEMO_SCHOOL; }

export function LibraryLoansPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;

  const schoolId = getSchoolId(user);

  const [loans,     setLoans]     = useState<Loan[]>([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [toast,     setToast]     = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [filterStatus, setFilterStatus] = useState('ACTIVE');
  const [filterMember, setFilterMember] = useState('');

  // Checkout form
  const [showCheckout, setShowCheckout] = useState(false);
  const [coForm,       setCoForm]       = useState({ memberId: '', itemId: '', method: 'MANUAL' });
  const [coError,      setCoError]      = useState<string | null>(null);
  const [checkingOut,  setCheckingOut]  = useState(false);

  // Fine payment form
  const [payingFineId, setPayingFineId] = useState<string | null>(null);
  const [fineAmount,   setFineAmount]   = useState('');
  const [payingFine,   setPayingFine]   = useState(false);

  // Inline action states
  const [returningId,  setReturningId]  = useState<string | null>(null);
  const [renewingId,   setRenewingId]   = useState<string | null>(null);

  const STATUS_CFG = Object.fromEntries(
    Object.entries(UI.statusLabels).map(([k, label]) => [k, { label, color: STATUS_COLORS[k] ?? '#9ca3af' }])
  );

  const load = useCallback(async (p: number) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ schoolId, page: String(p) });
      if (filterStatus) params.set('status', filterStatus);
      if (filterMember.trim()) params.set('memberId', filterMember.trim());
      const result = await apiGet<{ data: Loan[]; total: number }>(`/api/library/loans?${params}`);
      setLoans(result.data);
      setTotal(result.total);
      setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan pinjaman.');
    } finally { setLoading(false); }
  }, [schoolId, filterStatus, filterMember]);

  useEffect(() => { void load(1); }, [load]);

  async function handleCheckout() {
    if (!coForm.memberId.trim()) { setCoError('ID ahli diperlukan. / Member ID required.'); return; }
    if (!coForm.itemId.trim())   { setCoError('ID bahan diperlukan. / Item ID required.'); return; }
    setCheckingOut(true); setCoError(null);
    try {
      const result = await apiPost<{ loanId: string; dueDate: string }>('/api/library/loans/checkout', {
        memberId: coForm.memberId.trim(), itemId: coForm.itemId.trim(), checkoutMethod: coForm.method,
      });
      setToast({ variant: 'success', message: `Bahan berjaya dipinjam. Tarikh matang: ${result.dueDate}. / Item checked out. Due: ${result.dueDate}.` });
      setShowCheckout(false); setCoForm({ memberId: '', itemId: '', method: 'MANUAL' });
      void load(1);
    } catch (e) {
      setCoError(e instanceof Error ? e.message : 'Gagal meminjam. / Failed to checkout.');
    } finally { setCheckingOut(false); }
  }

  async function handleReturn(loanId: string) {
    setReturningId(loanId);
    try {
      const result = await apiPost<{ fine: number; finePaid: boolean }>(`/api/library/loans/${loanId}/return`, {});
      if (result.fine > 0) {
        setToast({ variant: 'error', message: `Bahan dipulangkan. Denda: BND ${result.fine.toFixed(2)}. / Item returned. Fine: BND ${result.fine.toFixed(2)}.` });
      } else {
        setToast({ variant: 'success', message: 'Bahan berjaya dipulangkan tanpa denda. / Item returned with no fine.' });
      }
      void load(page);
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal memulangkan.' });
    } finally { setReturningId(null); }
  }

  async function handleRenew(loanId: string) {
    setRenewingId(loanId);
    try {
      const result = await apiPost<{ newDueDate: string }>(`/api/library/loans/${loanId}/renew`, {});
      setToast({ variant: 'success', message: `Pinjaman diperbaharui. Tarikh matang baharu: ${result.newDueDate}. / Loan renewed. New due: ${result.newDueDate}.` });
      void load(page);
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal memperbaharui.' });
    } finally { setRenewingId(null); }
  }

  async function handlePayFine(loanId: string) {
    const amt = parseFloat(fineAmount);
    if (isNaN(amt) || amt <= 0) { setToast({ variant: 'error', message: 'Jumlah bayaran tidak sah. / Invalid payment amount.' }); return; }
    setPayingFine(true);
    try {
      await apiPost(`/api/library/loans/${loanId}/fines/pay`, { amount: amt, markPaid: true });
      setToast({ variant: 'success', message: `Denda BND ${amt.toFixed(2)} telah direkodkan. / Fine BND ${amt.toFixed(2)} recorded as paid.` });
      setPayingFineId(null); setFineAmount('');
      void load(page);
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal merekodkan bayaran.' });
    } finally { setPayingFine(false); }
  }

  const isOverdue = (loan: Loan) => !loan.return_date && new Date(loan.due_date) < new Date() && loan.status === 'ACTIVE';
  const totalPages = Math.ceil(total / 25) || 1;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>{UI.backLink}</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                📋 {UI.pageTitle}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {total} {UI.pageSubtitle}
              </p>
            </div>
            {!showCheckout && (
              <Button variant="primary" size="sm" onClick={() => { setShowCheckout(true); setCoError(null); }}>
                {UI.checkoutBtn}
              </Button>
            )}
          </div>

          {/* Checkout form */}
          {showCheckout && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{UI.checkoutFormTitle}</p>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
                <div style={{ flex: '2 1 180px' }}>
                  <label style={labelSt}>{UI.labelMemberId}</label>
                  <input type="text" value={coForm.memberId} onChange={(e) => setCoForm((f) => ({ ...f, memberId: e.target.value }))} placeholder="UUID ahli" disabled={checkingOut} style={inputSt()} />
                </div>
                <div style={{ flex: '2 1 180px' }}>
                  <label style={labelSt}>{UI.labelItemId}</label>
                  <input type="text" value={coForm.itemId} onChange={(e) => setCoForm((f) => ({ ...f, itemId: e.target.value }))} placeholder="UUID bahan" disabled={checkingOut} style={inputSt()} />
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <label style={labelSt}>{UI.labelMethod}</label>
                  <select value={coForm.method} onChange={(e) => setCoForm((f) => ({ ...f, method: e.target.value }))} disabled={checkingOut} style={inputSt()}>
                    {['MANUAL','BARCODE','RFID'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              {coError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{coError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <Button variant="primary" size="sm" onClick={() => void handleCheckout()} disabled={checkingOut} loading={checkingOut}>
                  {checkingOut ? UI.confirmingBtn : UI.confirmBtn}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowCheckout(false); setCoError(null); }} disabled={checkingOut}>{UI.cancelBtn}</Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg }}>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label style={labelSt}>{UI.filterStatus}</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} disabled={loading} style={inputSt()}>
                  <option value="">{UI.filterAll}</option>
                  {Object.entries(STATUS_CFG).map(([v, c]) => <option key={v} value={v}>{c.label}</option>)}
                </select>
              </div>
              <div style={{ flex: '2 1 180px' }}>
                <label style={labelSt}>{UI.filterMemberId}</label>
                <input type="text" value={filterMember} onChange={(e) => setFilterMember(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void load(1)} placeholder="UUID (pilihan)" disabled={loading} style={inputSt()} />
              </div>
              <Button variant="primary" size="sm" onClick={() => void load(1)} disabled={loading}>
                {loading ? <Spinner size="sm" /> : UI.filterBtn}
              </Button>
            </div>
          </div>

          {error && <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
            <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{error}</p>
            <Button variant="ghost" size="sm" onClick={() => void load(page)} style={{ marginTop: spacing.xs }}>{UI.retryBtn}</Button>
          </div>}

          {loading && loans.length === 0 && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>}

          {!loading && !error && loans.length === 0 && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>📋</div>
              <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{UI.emptyTitle}</p>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{UI.emptySubtitle}</p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {loans.map((loan) => {
              const sc        = STATUS_CFG[loan.status] ?? { label: loan.status, color: '#9ca3af' };
              const overdue   = isOverdue(loan);
              const hasFine   = parseFloat(loan.fine_amount) > 0;
              const isRet     = returningId === loan.id;
              const isRen     = renewingId  === loan.id;
              const isPaying  = payingFineId === loan.id;

              return (
                <div key={loan.id} style={{
                  backgroundColor: colors.neutral.charcoal,
                  borderRadius: borderRadius.xl,
                  border: `1px solid ${overdue ? 'rgba(248,113,113,0.4)' : hasFine && !loan.fine_paid ? 'rgba(250,204,21,0.3)' : colors.neutral.gray800}`,
                  overflow: 'hidden', boxShadow: shadows.lg,
                }}>
                  <div style={{ padding: spacing.lg, display: 'flex', gap: spacing.md, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ padding: '2px 8px', backgroundColor: `${sc.color}22`, color: sc.color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>
                          {sc.label}
                        </span>
                        {overdue && <span style={{ fontSize: typography.fontSize.xs, color: '#f87171', fontWeight: typography.fontWeight.bold }}>{UI.overdueBadge}</span>}
                        {hasFine && <span style={{ fontSize: typography.fontSize.xs, color: loan.fine_paid ? '#4ade80' : '#facc15' }}>
                          {loan.fine_paid ? UI.finePaidBadge : `⚠ Denda BND ${parseFloat(loan.fine_amount).toFixed(2)}`}
                        </span>}
                        {loan.checkout_method === 'RFID' && <span style={{ fontSize: typography.fontSize.xs, color: '#a78bfa' }}>📡 RFID</span>}
                      </div>
                      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {loan.title_en}
                      </p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {loan.catalogue_id} · {UI.memberLabel}: {loan.membership_number} ({loan.member_type})
                      </p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {UI.checkedOutLabel}: {new Date(loan.checkout_date).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}
                        {' · '}{UI.dueLabel}: <span style={{ color: overdue ? '#f87171' : colors.neutral.gray200 }}>{new Date(loan.due_date).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}</span>
                        {loan.return_date && ` · ${UI.returnedLabel}: ${new Date(loan.return_date).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}`}
                        {loan.renewal_count > 0 && ` · ${UI.renewedLabel} ${loan.renewal_count}×`}
                      </p>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap' }}>
                      {loan.status === 'ACTIVE' && (
                        <>
                          <Button variant="secondary" size="sm" onClick={() => void handleReturn(loan.id)} disabled={!!returningId || !!renewingId} loading={isRet}>
                            {isRet ? '…' : `↩ ${UI.returnBtn}`}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => void handleRenew(loan.id)} disabled={!!returningId || !!renewingId} loading={isRen}>
                            {isRen ? '…' : `↻ ${UI.renewBtn}`}
                          </Button>
                        </>
                      )}
                      {hasFine && !loan.fine_paid && (
                        <Button variant="ghost" size="sm" onClick={() => { setPayingFineId(payingFineId === loan.id ? null : loan.id); setFineAmount(loan.fine_amount); }}>
                          {UI.payFineBtn}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Fine payment panel */}
                  {isPaying && (
                    <div style={{ padding: spacing.lg, borderTop: `1px solid ${colors.neutral.gray800}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.sm, color: colors.neutral.white }}>
                        {UI.finePanelTitle} — BND {parseFloat(loan.fine_amount).toFixed(2)} {UI.finePanelOutstanding}
                      </p>
                      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end' }}>
                        <div>
                          <label style={labelSt}>{UI.fineAmountLabel}</label>
                          <input
                            type="number" value={fineAmount}
                            onChange={(e) => setFineAmount(e.target.value)}
                            min={0.01} step={0.01} max={parseFloat(loan.fine_amount)}
                            disabled={payingFine} style={{ ...inputSt(), width: '120px' }}
                          />
                        </div>
                        <Button variant="primary" size="sm" onClick={() => void handlePayFine(loan.id)} disabled={payingFine} loading={payingFine}>
                          {payingFine ? UI.fineRecordingBtn : UI.fineSaveBtn}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setPayingFineId(null)} disabled={payingFine}>{UI.cancelBtn}</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl }}>
              <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => void load(page - 1)}>{UI.prevBtn}</Button>
              <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages || loading} onClick={() => void load(page + 1)}>{UI.nextBtn}</Button>
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
